import {
  Button,
  Combobox,
  HelpText,
  Skeleton,
  TextField,
  UtilityButton,
} from '@lego/connect-components-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeField } from '../../components';
import { FileDropzone } from '../../components/fileDropzone';
import { useToastContext } from '../../providers/toast';
import type { LabelData, PrinterData, PrintFormState } from '../../types/types';
import {
  areRequiredLabelFieldsFilled,
  convertDateToISO,
  convertTimeTo24Hour,
  createUniqueId,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  formatDateWithInputFormat,
  formatTimeWithInputFormat,
  getTodayAsISO,
  isDate,
  isTime,
} from '../../utils/print';
import {
  sortRevisions,
  useFormState,
  useLabelRevision,
  usePrintData,
  useValidation,
} from './hooks';
import type { FieldConfig } from './schemas';

const defaultTime = '00:00';
const getInputType = (type: string): string => {
  if (type === 'Double') return 'number';
  if (isDate(type)) return 'date';
  if (isTime(type)) return 'time';
  return 'text';
};

const parseValue = (type: string, value: string) =>
  isDate(type) || isTime(type) ? value : type === 'String' ? String(value) : Number(value);

const getFileFieldValue = (field: FieldConfig, file: Record<string, string | number>) =>
  isDate(field.type)
    ? convertDateToISO(file[field.name] as string)
    : isTime(field.type)
      ? convertTimeTo24Hour(file[field.name] as string)
      : field.type === 'String'
        ? String(file[field.name])
        : Number(file[field.name]);

const getToastKeyForStatus = (status: string) =>
  status === 'completed'
    ? 'newPrintPage.response.printJobSuccess'
    : 'newPrintPage.response.printJobSubmitted';

const renderVariableInput = (
  id: string,
  variable: Record<string, string | number>,
  fieldConfig: FieldConfig,
  handleVariableChange: (id: string, fieldName: string, value: string | number) => void,
  formState: Pick<PrintFormState, 'validationErrors'>
) => {
  const { name, type, required, pickListValues, initialValue } = fieldConfig as FieldConfig;
  const errorMessage = formState.validationErrors[`${name}_${id}`];
  const value = variable[name] ?? initialValue ?? '';

  return (
    <div key={id + name} className="m-35">
      {Array.isArray(pickListValues) && pickListValues.length > 0 ? (
        <Combobox
          fullWidth
          value={value ? [String(value)] : []}
          onChange={(_e, items) => handleVariableChange(id as string, name, items[0]?.value ?? '')}
          placeholder={`${name} (${type})`}
          size="medium"
          required={required}
          invalid={!!errorMessage}
        >
          {pickListValues.map((val: string) => (
            <Combobox.Option key={val} value={val}>
              {val}
            </Combobox.Option>
          ))}
        </Combobox>
      ) : getInputType(type) === 'text' ? (
        <TextField
          size="medium"
          value={value as string}
          onChange={(_e, v) => handleVariableChange(id as string, name, parseValue(type, v))}
          validationState={errorMessage ? 'invalid' : undefined}
          required={required}
          placeholder={`${name} (${type})`}
        />
      ) : (
        <NativeField
          title={(value as string) || ''}
          value={value as string}
          onChange={(e) => {
            let v = e.target.value;
            if (isTime(fieldConfig.type) && !v) v = defaultTime;
            handleVariableChange(id as string, name, parseValue(type, v));
          }}
          onBlur={(e) => {
            let v = e.target.value;
            if (isTime(fieldConfig.type) && !v) v = defaultTime;
            handleVariableChange(id as string, name, parseValue(type, v));
          }}
          validationState={errorMessage ? 'invalid' : undefined}
          required={required}
          type={getInputType(type)}
        />
      )}
    </div>
  );
};

export const PrintPage = () => {
  const { t } = useTranslation();
  const { printers, labels, printersLoading, labelsLoading, getLabelRevision, submitPrintJob } =
    usePrintData();
  const { formState, updateFormState, resetForm } = useFormState();
  const { showToast } = useToastContext();

  const initialRowSet = () => ({ quantity: 1, id: createUniqueId() });

  const labelRevisionData = useLabelRevision(
    formState.selectedLabel,
    formState.selectedRevision,
    getLabelRevision
  );

  const labelRevisionDataWithQuantity = useMemo(() => {
    if (!labelRevisionData) return labelRevisionData;
    const fields = (labelRevisionData.fields || []).filter((f) => f.name !== 'quantity');
    return { ...labelRevisionData, fields };
  }, [labelRevisionData]);

  const areMandatoryFieldsFilled = areRequiredLabelFieldsFilled(
    labelRevisionDataWithQuantity,
    formState.variables
  );

  const isQuantityOnly =
    labelRevisionDataWithQuantity && labelRevisionDataWithQuantity.fields.length === 0;

  const { validateField, validateForm } = useValidation(labelRevisionDataWithQuantity);

  const selectedLabelData = formState.selectedLabel
    ? labels?.items.find((l: LabelData) => l.id === formState.selectedLabel)
    : null;

  const labelRevisions =
    formState.selectedLabel && selectedLabelData
      ? sortRevisions(
          (selectedLabelData.activeRevisions?.length ?? 0) > 0
            ? (selectedLabelData.activeRevisions ?? [])
            : (selectedLabelData.availableRevisions ?? [])
        )
      : [];

  useEffect(() => {
    if (!labelRevisionDataWithQuantity) {
      updateFormState({ variables: [], validationErrors: {} });
      return;
    }

    const initialVars: Record<string, string | number> = { id: createUniqueId(), quantity: 1 };

    Object.values(labelRevisionDataWithQuantity.fields || {}).forEach((fieldConfig) => {
      let value = fieldConfig.initialValue
        ? parseValue(fieldConfig.type, fieldConfig.initialValue)
        : '';

      if (isDate(fieldConfig.type)) {
        value = !value ? getTodayAsISO() : convertDateToISO(value as string);
      }

      if (isTime(fieldConfig.type)) {
        value = !value ? defaultTime : convertTimeTo24Hour(value as string);
      }

      initialVars[fieldConfig.name] = value;
    });

    updateFormState({ variables: [initialVars], validationErrors: {} });
  }, [labelRevisionDataWithQuantity, updateFormState]);

  const handlePrinterChange = (value: string) => {
    updateFormState({ selectedPrinter: value ? Number(value) : null });
  };

  const handleLabelChange = (value: string) => {
    const newLabelId = value ? Number(value) : null;
    updateFormState({
      selectedLabel: newLabelId,
      selectedRevision: '',
    });

    if (newLabelId && labels) {
      const labelData = labels.items.find((l: LabelData) => l.id === newLabelId);
      if (labelData && labelData.availableRevisions.length > 0) {
        const sortedRevisions = sortRevisions(labelData.availableRevisions);
        updateFormState({ selectedRevision: sortedRevisions[0] });
      }
    }
  };

  const prepareVariablesForApi = () => {
    const fields = Object.values(labelRevisionDataWithQuantity?.fields || {});
    const variables = formState.variables;
    return variables.map(({ id, ...rest }) => {
      fields.forEach((field) => {
        if (rest[field.name]) {
          if (isDate(field.type)) {
            rest[field.name] = formatDateWithInputFormat(
              rest[field.name],
              field.inputFormat || DEFAULT_DATE_FORMAT
            );
          } else if (isTime(field.type)) {
            rest[field.name] = formatTimeWithInputFormat(
              rest[field.name],
              field.inputFormat || DEFAULT_TIME_FORMAT
            );
          }
        }
      });

      return rest;
    });
  };

  const handleRevisionChange = (value: string) => {
    updateFormState({ selectedRevision: value });
  };

  const handleVariableChange = (id: string, fieldName: string, value: string | number) => {
    updateFormState({
      variables: formState.variables.map((vars) => {
        if (vars.id === id) {
          return { ...vars, [fieldName]: value };
        }
        return vars;
      }),
    });

    validateField(fieldName, id, value, (fn) => {
      const newErrors = fn(formState.validationErrors);
      updateFormState({ validationErrors: newErrors });
    });
  };

  const handleRemoveVariableSet = (id: string) => {
    const validationErrors = { ...formState.validationErrors };

    Object.keys(validationErrors).forEach((key) => {
      if (key.endsWith(`_${id}`)) {
        delete validationErrors[key];
      }
    });

    updateFormState({
      variables: formState.variables.filter((variable) => variable.id !== id),
      validationErrors,
    });
  };

  const handleAddVariableSet = () => {
    updateFormState({
      variables: formState.variables.concat(initialRowSet()),
    });
  };

  const handlePrint = async () => {
    if (!formState.selectedPrinter || !formState.selectedLabel || !formState.selectedRevision) {
      showToast(t('newPrintPage.response.fieldsMissing'), 'warning');
      return;
    }

    const validation = validateForm(formState.variables, (fn) => {
      const newErrors = fn(formState.validationErrors);
      updateFormState({ validationErrors: newErrors });
    });

    if (!validation?.isValid) {
      const errorMessage = validation.toastMessage || t('newPrintPage.response.fixValidation');
      showToast(errorMessage, 'warning');
      return;
    }

    updateFormState({ isPrinting: true });
    try {
      const quantity = formState.variables.reduce((acc, vars) => {
        const rowQuantity = Number(vars.quantity ?? 0);
        return acc + (Number.isFinite(rowQuantity) ? rowQuantity : 0);
      }, 0);

      const result = await submitPrintJob({
        printerId: formState.selectedPrinter,
        labelId: formState.selectedLabel,
        labelRevision: formState.selectedRevision,
        variables: prepareVariablesForApi(),
        quantity,
        preview: false,
      });

      if (result.status === 'failed') {
        showToast(`Print job failed: ${result.errorMessage}`, 'error');
        return;
      }

      resetForm();
      const toastKey = result.accepted
        ? 'newPrintPage.response.printJobSubmitted'
        : getToastKeyForStatus(result.status);
      showToast(t(toastKey, { count: quantity }), 'success');
    } catch (error) {
      console.error('Print job failed:', error);
      const message =
        error instanceof Error ? error.message : t('newPrintPage.response.printJobConnectionError');
      showToast(message, 'error');
    } finally {
      updateFormState({ isPrinting: false });
    }
  };

  if (!printers?.items || printersLoading || !labels?.items || labelsLoading) {
    return (
      <div
        className="bg-core-slate-50 min-h-screen px-400 py-400"
        role="status"
        aria-label={t(
          !printers?.items || printersLoading
            ? 'newPrintPage.action.loadingPrinters'
            : 'newPrintPage.action.loadingLabels'
        )}
      >
        <div className="mx-auto flex max-w-xl flex-col items-center gap-300">
          <Skeleton width="18rem" height="3rem" showAnimation />
          <Skeleton width="24rem" height="1.5rem" showAnimation />
          <Skeleton width="12rem" height="1.5rem" showAnimation />
          <Skeleton width="100%" height="2.5rem" showAnimation />
        </div>
      </div>
    );
  }

  const renderLabelSelection = () => {
    if (!formState.selectedPrinter) return null;

    return (
      <>
        <h2 className="text-heading-md font-bold text-core-slate-1100">
          {t('newPrintPage.display.selectLabel')}
        </h2>

        <div className="grid gap-150 md:grid-cols-2 gap-6 w-2/5">
          <Combobox
            placeholder={t('newPrintPage.display.chooseLabel')}
            fullWidth
            size="medium"
            value={formState.selectedLabel ? [formState.selectedLabel.toString()] : []}
            onChange={(_e, items) => handleLabelChange(items[0]?.value ?? '')}
          >
            {(labels.items ?? []).map((label: LabelData) => (
              <Combobox.Option key={label.id?.toString() ?? ''} value={label.id?.toString() ?? ''}>
                {label.name}
              </Combobox.Option>
            ))}
          </Combobox>

          {formState.selectedLabel && (
            <div>
              <Combobox
                placeholder={t('newPrintPage.display.selectRevision')}
                fullWidth
                size="medium"
                disabled={labelRevisions.length === 0}
                value={formState.selectedRevision ? [formState.selectedRevision] : []}
                onChange={(_e, items) => handleRevisionChange(items[0]?.value ?? '')}
              >
                {labelRevisions.map((revision) => (
                  <Combobox.Option key={revision} value={revision}>
                    {revision}
                  </Combobox.Option>
                ))}
              </Combobox>
              {labelRevisions.length === 0 && (
                <HelpText variant="negative" size="small">
                  {t('newPrintPage.display.noRevisionsAvailable')}
                </HelpText>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="px-[30px] my-200 flex flex-col justify-center items-center gap-150">
        <h1 className="text-heading-3xl font-brand font-bold mb-4 text-core-blue-900 text-center">
          🧙‍♂️ {t('newPrintPage.display.labelingWizardTitle')}
        </h1>
        <p className="text-body-md text-core-slate-1100 text-center">
          {t('newPrintPage.display.labelingWizardDescription')}
        </p>

        <h2 className="text-heading-md font-bold text-core-slate-1100">
          {t('newPrintPage.display.selectPrinter')}
        </h2>

        <div className="w-2/5">
          <Combobox
            placeholder={t('newPrintPage.display.choosePrinter')}
            fullWidth
            size="medium"
            value={formState.selectedPrinter ? [formState.selectedPrinter.toString()] : []}
            onChange={(_e, items) => handlePrinterChange(items[0]?.value ?? '')}
          >
            {(printers.items ?? [])
              .filter((printer: PrinterData) => !printer.deletedAt)
              .map((printer: PrinterData) => (
                <Combobox.Option
                  key={printer.id?.toString() ?? ''}
                  value={printer.id?.toString() ?? ''}
                >
                  {`${printer.name} (${printer.manufacturer} ${printer.model})`}
                </Combobox.Option>
              ))}
          </Combobox>
        </div>

        {renderLabelSelection()}
        {labelRevisionDataWithQuantity && !isQuantityOnly && (
          <>
            <div className="w-[40%]">
              <FileDropzone
                onFilesAccepted={() => {}}
                onParseError={() => {
                  showToast(t('newPrintPage.display.parseFilesError'), 'error');
                }}
                onFilesParsed={(files) => {
                  const allowedFields = Object.values(labelRevisionDataWithQuantity.fields).map(
                    (f) => f.name
                  );
                  let validationErrors = {};
                  const parsedFiles = files.map((file) => {
                    const parsedQuantity = Number(file.quantity ?? 1);
                    const filtered: Record<string, string | number> = {
                      quantity: Number.isFinite(parsedQuantity) ? parsedQuantity : 1,
                      id: createUniqueId(),
                    };
                    labelRevisionDataWithQuantity.fields.forEach((field) => {
                      if (allowedFields.includes(field.name) && file[field.name] !== undefined) {
                        filtered[field.name] = getFileFieldValue(field, file);

                        validateField(
                          field.name,
                          filtered.id as string,
                          filtered[field.name],
                          (fn) => {
                            validationErrors = fn(validationErrors);
                          }
                        );
                      }
                    });

                    return filtered;
                  });

                  updateFormState({
                    variables: parsedFiles.length ? parsedFiles : [initialRowSet()],
                    validationErrors,
                  });
                }}
              />
            </div>
            <h2 className="text-heading-md font-bold text-core-slate-1100">
              {t('newPrintPage.display.or')}
            </h2>
            <h2 className="text-heading-md font-bold text-core-slate-1100">
              {t('newPrintPage.display.fillVariables')}
            </h2>

            <div className="flex justify-center" style={{ maxWidth: '80%' }}>
              <div className="flex overflow-x-auto w-full">
                {Object.entries(labelRevisionDataWithQuantity.fields).map(
                  ([fieldKey, fieldConfig]) => {
                    const name =
                      fieldConfig.name.charAt(0).toUpperCase() + fieldConfig.name.slice(1);
                    const fieldErrors = formState.variables
                      .map(({ id }) => formState.validationErrors[`${fieldConfig.name}_${id}`])
                      .filter(Boolean);
                    return (
                      <div key={fieldKey} className="flex flex-col">
                        <div className="m-35 bg-[#D9D9D994] flex items-center justify-start px-[15px] py-100 h-[43px] text-body-sm truncate">
                          {name}
                        </div>
                        <div>
                          {formState.variables.map(({ id, ...variable }) =>
                            renderVariableInput(
                              id as string,
                              variable,
                              fieldConfig,
                              handleVariableChange,
                              formState
                            )
                          )}
                        </div>
                        {fieldErrors.length > 0 && (
                          <div className="mx-35">
                            <HelpText variant="negative" size="small" className="break-words">
                              {fieldErrors[0]}
                            </HelpText>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}

                {/* Quantity column */}
                <div className="flex flex-col">
                  <div className="m-35 bg-core-blue-100 flex items-center justify-start px-[15px] py-100 h-[43px] text-body-sm truncate">
                    {t('newPrintPage.display.quantity')}
                  </div>
                  <div>
                    {formState.variables.map((variable, index) => (
                      <div key={`qty-${variable.id}`} className="m-35 flex items-center gap-100">
                        <div className="min-w-0 flex-1">
                          <NativeField
                            type="number"
                            min={1}
                            step={1}
                            validationState={
                              (Number(variable.quantity) || 0) < 1 ? 'invalid' : undefined
                            }
                            aria-label={t('newPrintPage.display.quantity')}
                            value={String(variable.quantity ?? '')}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              handleVariableChange(
                                variable.id as string,
                                'quantity',
                                Number.isFinite(v) ? v : 0
                              );
                            }}
                          />
                        </div>
                        {formState.variables.length > 1 && (
                          <UtilityButton
                            iconName="trash"
                            appearance="ghost"
                            size="small"
                            aria-label={t('newPrintPage.display.removeVariableSet', {
                              number: index + 1,
                            })}
                            onClick={() => handleRemoveVariableSet(variable.id as string)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {formState.variables.some((v) => (Number(v.quantity) || 0) < 1) && (
                    <div className="mx-35">
                      <HelpText variant="negative" size="small" className="whitespace-nowrap">
                        {t('formPrintPage.quantityMinError', 'Min 1')}
                      </HelpText>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Button onClick={handleAddVariableSet} variant="neutral" appearance="ghost">
              <div className="bg-core-white flex items-center justify-center w-500 h-500 rounded-full border-2 border-[#3973f7] mr-[10px]">
                <div className="text-[30px] leading-none text-[#3973f7] font-medium">+</div>
              </div>
              {t('newPrintPage.display.addVariableSet')}
            </Button>
          </>
        )}
        {labelRevisionData?.preview && (
          <>
            <h2 className="text-heading-md font-bold text-core-slate-1100">
              {t('newPrintPage.display.labelPreview')}
            </h2>

            <div className="flex flex-col justify-center">
              <div className="text-center text-body-xs text-core-slate-700 italic mt-1 mb-[10px]">
                ℹ️ {t('newPrintPage.display.labelPreviewNote')}
              </div>
              <div className="w-48 h-48 border-2 border-dashed border-core-slate-200 rounded-150 flex flex-col items-center justify-center bg-core-slate-50 overflow-hidden">
                <img
                  src={`data:image/png;base64,${labelRevisionData.preview}`}
                  alt="Label Preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
            <div className="text-center mt-3 text-body-xs text-core-slate-1100">
              {t('formPrintPage.labelSize', 'Label size:')} {labelRevisionData.width} ×{' '}
              {labelRevisionData.height} mm
            </div>
          </>
        )}

        <div className="flex items-end justify-center gap-200">
          {isQuantityOnly && (
            <div>
              <span className="text-body-xs font-medium text-core-slate-900">
                {t('printJobsPage.display.quantity', 'Quantity')}
              </span>
              <div className="mt-100 w-[100px]">
                <NativeField
                  type="number"
                  min={1}
                  step={1}
                  validationState={
                    (Number(formState.variables[0]?.quantity) || 0) < 1 ? 'invalid' : undefined
                  }
                  aria-label={t('printJobsPage.display.quantity', 'Quantity')}
                  value={String(formState.variables[0]?.quantity ?? '')}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleVariableChange(
                      formState.variables[0]?.id as string,
                      'quantity',
                      Number.isFinite(v) ? v : 0
                    );
                  }}
                />
              </div>
              {(Number(formState.variables[0]?.quantity) || 0) < 1 && (
                <HelpText variant="negative" size="small" className="whitespace-nowrap">
                  {t('formPrintPage.quantityMinError', 'Quantity must be at least 1')}
                </HelpText>
              )}
            </div>
          )}
          <Button
            variant="primary"
            onClick={handlePrint}
            size="small"
            iconName="print"
            loading={formState.isPrinting}
            loadingText={t('newPrintPage.display.printing')}
            disabled={
              formState.isPrinting ||
              !formState.selectedPrinter ||
              !formState.selectedLabel ||
              !labelRevisionData ||
              !areMandatoryFieldsFilled ||
              formState.variables.some((v) => (Number(v.quantity) || 0) < 1)
            }
          >
            {t('newPrintPage.display.print')}
          </Button>
        </div>
      </div>
    </>
  );
};
